// Page de création de post - DEUX PARCOURS
// Particulier vs Professionnel
// Avec vérification d'identité pour lutter contre les arnaques

import { useState, useRef, useEffect } from 'react';
import { useNavigate, useOutletContext, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Image, X, MapPin, Hash, ChevronLeft, Send, Camera, Video, Briefcase,
  Package, Hammer, Map, Users, ArrowRight, Check, ChevronRight,
  Building2, Sparkles, HelpCircle, Search, Plus, Layers, Star,
  Clock, Eye, Zap, Droplets, Flame, TreeDeciduous, HardHat, Tag, ShoppingBag,
  Shield, UserCheck, BadgeCheck, Lock, FileText, Smartphone, Network
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';
import UnifiedHeader from '../../../components/layout/UnifiedHeader';
import MainNavigation from '../../../components/layout/MainNavigation';

import { API_URL } from '../../../config/api';

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PAGE DE CERTIFICATION - Pour non-connectés (Réseau Social)
// Design unifié avec le reste du site AbracadaBati
// ══════════════════════════════════════════════════════════════════════════════
function SocialCertificationPage() {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-[#F8FAFC] font-['Inter',sans-serif]">
      {/* Header Unifié - Même design que FeedPage et le reste du site */}
      <UnifiedHeader />
      
      {/* Navigation Principale - Même design que FeedPage */}
      <MainNavigation />

      {/* Spacer Mobile - Compense les éléments fixed */}
      <div className="h-24 md:hidden" />

      <main className="max-w-3xl mx-auto px-4 py-8 sm:py-12 relative">
        {/* Bouton fermer (X) - Retour à la page précédente */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 right-4 sm:top-8 sm:right-0 w-10 h-10 flex items-center justify-center rounded-full bg-white border border-gray-200 shadow-sm hover:bg-gray-50 hover:border-gray-300 transition-all z-10"
          aria-label="Fermer"
        >
          <X size={20} className="text-gray-500" />
        </button>
        
        {/* Header avec icône */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[#1E40AF] to-[#3B82F6] mb-6 shadow-lg shadow-blue-500/20">
            <Network size={40} className="text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
            Rejoins la communauté
          </h1>
          <p className="text-gray-600 text-base sm:text-lg max-w-xl mx-auto">
            Pour publier sur le réseau social AbracadaBati, nous vérifions l'identité de chaque membre afin de garantir un espace d'échange authentique et sécurisé.
          </p>
        </motion.div>

        {/* Carte principale */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
        >
          {/* Bandeau vert */}
          <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-6 py-4 text-white">
            <div className="flex items-center gap-3">
              <BadgeCheck size={24} />
              <div>
                <p className="font-semibold">Une personne = Un compte vérifié</p>
                <p className="text-emerald-100 text-sm">Chaque membre est certifié comme une vraie personne</p>
              </div>
            </div>
          </div>

          <div className="p-6 sm:p-8">
            {/* Processus de vérification */}
            <h2 className="text-lg font-bold text-gray-900 mb-4">Comment ça marche ?</h2>
            
            <div className="grid sm:grid-cols-3 gap-4 mb-8">
              <div className="text-center p-4">
                <div className="w-12 h-12 rounded-full bg-[#1E40AF]/10 flex items-center justify-center mx-auto mb-3">
                  <span className="text-[#1E40AF] font-bold">1</span>
                </div>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <FileText size={18} className="text-gray-500" />
                  <h3 className="font-semibold text-gray-900">Pièce d'identité</h3>
                </div>
                <p className="text-gray-500 text-sm">Photo de ta CNI ou passeport</p>
              </div>

              <div className="text-center p-4">
                <div className="w-12 h-12 rounded-full bg-[#1E40AF]/10 flex items-center justify-center mx-auto mb-3">
                  <span className="text-[#1E40AF] font-bold">2</span>
                </div>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Smartphone size={18} className="text-gray-500" />
                  <h3 className="font-semibold text-gray-900">Selfie de vérification</h3>
                </div>
                <p className="text-gray-500 text-sm">Pour confirmer que c'est bien toi</p>
              </div>

              <div className="text-center p-4">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                  <Check size={20} className="text-emerald-600" />
                </div>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <BadgeCheck size={18} className="text-emerald-600" />
                  <h3 className="font-semibold text-gray-900">Compte certifié</h3>
                </div>
                <p className="text-gray-500 text-sm">Publie librement sur le réseau</p>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center mb-8">
              <Link to="/inscription">
                <Button className="h-14 px-8 bg-gradient-to-r from-[#F97316] to-[#EA580C] hover:from-[#EA580C] hover:to-[#DC2626] text-white rounded-xl font-bold text-lg shadow-lg shadow-orange-500/25 transition-all">
                  Inscris-toi gratuitement
                  <ChevronRight size={20} className="ml-2" />
                </Button>
              </Link>
              <p className="text-gray-500 text-sm mt-3">
                Déjà inscrit ? <Link to="/connexion" className="text-[#1E40AF] font-medium hover:underline">Connecte-toi</Link>
              </p>
            </div>

            {/* Avantages */}
            <h2 className="text-lg font-bold text-gray-900 mb-4">Pourquoi un réseau vérifié ?</h2>
            
            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-xl">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Shield size={20} className="text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Zéro faux profils</h3>
                  <p className="text-gray-600 text-sm">Chaque membre est une personne réelle identifiée. Fini les bots et les imposteurs.</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-emerald-50 rounded-xl">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <UserCheck size={20} className="text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Échanges authentiques</h3>
                  <p className="text-gray-600 text-sm">Partage tes réalisations et conseils avec de vrais professionnels et passionnés.</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-orange-50 rounded-xl">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <Lock size={20} className="text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Protection contre les arnaques</h3>
                  <p className="text-gray-600 text-sm">En cas de contenu frauduleux, nous pouvons identifier et agir contre les responsables.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer RGPD */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-100">
            <div className="flex items-start gap-3">
              <Lock size={16} className="text-gray-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-gray-500 leading-relaxed">
                <strong>Protection de tes données :</strong> Tes documents d'identité sont traités de manière sécurisée et conforme au RGPD. 
                Ils sont utilisés uniquement pour la vérification de ton identité et ne sont jamais partagés avec des tiers.
                <Link to="/confidentialite" className="text-[#1E40AF] hover:underline ml-1">En savoir plus</Link>
              </p>
            </div>
          </div>
        </motion.div>

        {/* Badges de confiance */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-8 flex flex-wrap items-center justify-center gap-6 text-gray-400"
        >
          <div className="flex items-center gap-2">
            <Shield size={18} />
            <span className="text-sm">Données sécurisées</span>
          </div>
          <div className="flex items-center gap-2">
            <Lock size={18} />
            <span className="text-sm">Conforme RGPD</span>
          </div>
          <div className="flex items-center gap-2">
            <BadgeCheck size={18} />
            <span className="text-sm">100% vérifiés</span>
          </div>
        </motion.div>
      </main>
      
      {/* Spacer pour la navigation mobile */}
      <div className="h-20 lg:hidden" />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// COMPOSANT TAG PRODUIT SUR IMAGE - Style Instagram Shopping
// ═══════════════════════════════════════════════════════════════

function ProductTagOverlay({ 
  imageUrl, 
  tags, 
  onAddTag, 
  onRemoveTag, 
  isEditing = true,
  products = [] 
}) {
  const [showTagForm, setShowTagForm] = useState(false);
  const [clickPosition, setClickPosition] = useState(null);
  const [newTagName, setNewTagName] = useState('');
  const [newTagPrice, setNewTagPrice] = useState('');
  const imageRef = useRef(null);

  const handleImageClick = (e) => {
    if (!isEditing) return;
    
    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    setClickPosition({ x, y });
    setShowTagForm(true);
    setNewTagName('');
    setNewTagPrice('');
  };

  const handleAddTag = () => {
    if (!newTagName.trim()) {
      toast.error('Entrez un nom de produit');
      return;
    }
    
    const newTag = {
      id: crypto.randomUUID(),
      product_id: crypto.randomUUID(),
      product_type: 'product',
      product_name: newTagName.trim(),
      product_price: parseFloat(newTagPrice) || null,
      x_percent: clickPosition.x,
      y_percent: clickPosition.y,
    };
    
    onAddTag(newTag);
    setShowTagForm(false);
    setClickPosition(null);
    toast.success('Tag produit ajouté !');
  };

  return (
    <div className="relative w-full h-full">
      {/* Image avec zone cliquable */}
      <div 
        ref={imageRef}
        className={`relative w-full h-full ${isEditing ? 'cursor-crosshair' : ''}`}
        onClick={handleImageClick}
      >
        <img src={imageUrl} alt="" className="w-full h-full object-cover" />
        
        {/* Overlay pour indiquer qu'on peut cliquer */}
        {isEditing && tags.length === 0 && (
          <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
            <div className="bg-white/90 rounded-xl px-4 py-2 flex items-center gap-2">
              <Tag size={18} className="text-blue-600" />
              <span className="text-sm font-medium text-gray-700">Cliquez pour ajouter un tag</span>
            </div>
          </div>
        )}
        
        {/* Tags existants */}
        {tags.map((tag, index) => (
          <motion.div
            key={tag.id || index}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            className="absolute z-10"
            style={{ 
              left: `${tag.x_percent}%`, 
              top: `${tag.y_percent}%`,
              transform: 'translate(-50%, -100%)'
            }}
          >
            {/* Point indicateur */}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full">
              <div className="w-3 h-3 bg-white rounded-full shadow-lg border-2 border-blue-500" />
            </div>
            
            {/* Bulle tag */}
            <div className="bg-white rounded-xl shadow-xl px-3 py-2 min-w-[120px] relative">
              <div className="flex items-start gap-2">
                <ShoppingBag size={14} className="text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 text-xs leading-tight truncate">
                    {tag.product_name}
                  </p>
                  {tag.product_price && (
                    <p className="text-blue-600 font-bold text-xs mt-0.5">
                      {tag.product_price.toFixed(2)} €
                    </p>
                  )}
                </div>
                {isEditing && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveTag(tag.id);
                    }}
                    className="w-5 h-5 rounded-full bg-red-100 hover:bg-red-200 flex items-center justify-center flex-shrink-0"
                  >
                    <X size={12} className="text-red-600" />
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      
      {/* Formulaire d'ajout de tag (popup) */}
      <AnimatePresence>
        {showTagForm && clickPosition && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/40 z-20"
              onClick={() => setShowTagForm(false)}
            />
            
            {/* Formulaire */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 10 }}
              className="absolute z-30 bg-white rounded-2xl shadow-2xl p-4 w-64"
              style={{
                left: `${Math.min(Math.max(clickPosition.x, 30), 70)}%`,
                top: `${Math.min(Math.max(clickPosition.y, 20), 60)}%`,
                transform: 'translate(-50%, -50%)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Tag size={16} className="text-blue-600" />
                </div>
                <h4 className="font-bold text-gray-900 text-sm">Ajouter un produit</h4>
              </div>
              
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Nom du produit</label>
                  <input
                    type="text"
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    placeholder="Ex: Carrelage marbre..."
                    className="w-full px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    autoFocus
                  />
                </div>
                
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Prix (optionnel)</label>
                  <input
                    type="number"
                    value={newTagPrice}
                    onChange={(e) => setNewTagPrice(e.target.value)}
                    placeholder="Ex: 45.00"
                    className="w-full px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => setShowTagForm(false)}
                  className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleAddTag}
                  className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium text-white transition-colors"
                >
                  Ajouter
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

// Types de publications pour PARTICULIERS
const PARTICULIER_TYPES = [
  {
    id: 'photo_video',
    label: 'Photo / Vidéo',
    description: 'Partage tes photos et vidéos',
    icon: Image,
    color: '#22C55E',
    bgColor: '#DCFCE7'
  },
  {
    id: 'avant_apres',
    label: 'Avant / Après',
    description: 'Montrez une transformation',
    icon: Sparkles,
    color: '#8B5CF6',
    bgColor: '#EDE9FE'
  },
  {
    id: 'recherche_emploi',
    label: 'Recherche d\'emploi',
    description: 'Trouvez un emploi dans le bâtiment',
    icon: Search,
    color: '#0EA5E9',
    bgColor: '#E0F2FE'
  },
  {
    id: 'question',
    label: 'Question / Conseil',
    description: 'Posez une question à la communauté',
    icon: HelpCircle,
    color: '#F59E0B',
    bgColor: '#FEF3C7'
  },
];

// Types de publications pour PROFESSIONNELS
const PROFESSIONNEL_TYPES = [
  {
    id: 'chantier',
    label: 'Chantier en cours',
    description: 'Affiche ton chantier sur la carte',
    icon: Map,
    color: '#EF4444',
    bgColor: '#FEE2E2',
    isPro: true
  },
  {
    id: 'produit',
    label: 'Produit',
    description: 'Présente un produit de ton catalogue',
    icon: Package,
    color: '#3B82F6',
    bgColor: '#DBEAFE',
    isPro: true
  },
  {
    id: 'prestation',
    label: 'Prestation / Service',
    description: 'Mets en avant tes services',
    icon: Hammer,
    color: '#F97316',
    bgColor: '#FFEDD5',
    isPro: true
  },
  {
    id: 'avant_apres',
    label: 'Avant / Après',
    description: 'Montrez une réalisation',
    icon: Sparkles,
    color: '#8B5CF6',
    bgColor: '#EDE9FE'
  },
  {
    id: 'offre_emploi',
    label: 'Offre d\'emploi',
    description: 'Recrute dans ton équipe',
    icon: Users,
    color: '#10B981',
    bgColor: '#D1FAE5',
    isPro: true
  },
  {
    id: 'conseil',
    label: 'Conseil / Astuce',
    description: 'Partage ton expertise',
    icon: Star,
    color: '#EAB308',
    bgColor: '#FEF9C3'
  },
  {
    id: 'photo_video',
    label: 'Photo / Vidéo',
    description: 'Publication libre',
    icon: Image,
    color: '#22C55E',
    bgColor: '#DCFCE7'
  },
];

// Métiers pour le chantier
const METIERS = [
  { id: 'pisciniste', label: 'Pisciniste', icon: Droplets, color: '#0EA5E9' },
  { id: 'plombier', label: 'Plombier', icon: Droplets, color: '#0EA5E9' },
  { id: 'electricien', label: 'Électricien', icon: Zap, color: '#EAB308' },
  { id: 'macon', label: 'Maçon', icon: HardHat, color: '#F97316' },
  { id: 'chauffagiste', label: 'Chauffagiste', icon: Flame, color: '#EF4444' },
  { id: 'menuisier', label: 'Menuisier', icon: Layers, color: '#92400E' },
  { id: 'paysagiste', label: 'Paysagiste', icon: TreeDeciduous, color: '#22C55E' },
  { id: 'carreleur', label: 'Carreleur', icon: Layers, color: '#06B6D4' },
  { id: 'peintre', label: 'Peintre', icon: Layers, color: '#EC4899' },
  { id: 'couvreur', label: 'Couvreur', icon: Building2, color: '#8B5CF6' },
];

// Villes françaises pour l'autocomplétion
const VILLES = [
  { name: 'Paris', lat: 48.8566, lng: 2.3522 },
  { name: 'Lyon', lat: 45.7640, lng: 4.8357 },
  { name: 'Marseille', lat: 43.2965, lng: 5.3698 },
  { name: 'Toulouse', lat: 43.6047, lng: 1.4442 },
  { name: 'Nice', lat: 43.7102, lng: 7.2620 },
  { name: 'Nantes', lat: 47.2184, lng: -1.5536 },
  { name: 'Montpellier', lat: 43.6108, lng: 3.8767 },
  { name: 'Bordeaux', lat: 44.8378, lng: -0.5792 },
  { name: 'Lille', lat: 50.6292, lng: 3.0573 },
  { name: 'Strasbourg', lat: 48.5734, lng: 7.7521 },
  { name: 'Rennes', lat: 48.1173, lng: -1.6778 },
  { name: 'Grenoble', lat: 45.1885, lng: 5.7245 },
];

// ═══════════════════════════════════════════════════════════════
// ÉTAPE 1 : Sélection du type de publication
// ═══════════════════════════════════════════════════════════════
function TypeSelectionStep({ userType, onSelectType }) {
  const types = userType === 'professionnel' ? PROFESSIONNEL_TYPES : PARTICULIER_TYPES;
  
  return (
    <div className="p-4">
      <div className="text-center mb-6">
        <h2 className="text-xl font-bold text-gray-900">Que souhaitez-vous publier ?</h2>
        <p className="text-gray-500 text-sm mt-1">
          {userType === 'professionnel' 
            ? 'Choisissez le type de contenu professionnel'
            : 'Sélectionnez le type de publication'
          }
        </p>
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        {types.map((type) => {
          const Icon = type.icon;
          return (
            <motion.button
              key={type.id}
              onClick={() => onSelectType(type)}
              className="flex flex-col items-start p-4 rounded-2xl border-2 border-transparent hover:border-gray-200 transition-all text-left"
              style={{ backgroundColor: type.bgColor }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid={`post-type-${type.id}`}
            >
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-3"
                style={{ backgroundColor: 'white' }}
              >
                <Icon size={24} style={{ color: type.color }} />
              </div>
              <h3 className="font-semibold text-gray-900 text-sm">{type.label}</h3>
              <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{type.description}</p>
              {type.isPro && (
                <span className="mt-2 px-2 py-0.5 bg-white/80 rounded-full text-[10px] font-semibold text-gray-600">
                  PRO
                </span>
              )}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ÉTAPE 2 : Formulaire selon le type
// ═══════════════════════════════════════════════════════════════
function PostFormStep({ 
  postType, 
  profile, 
  onBack, 
  onSubmit, 
  loading 
}) {
  const fileInputRef = useRef(null);
  const [content, setContent] = useState('');
  const [media, setMedia] = useState([]);
  const [location, setLocation] = useState('');
  const [selectedCity, setSelectedCity] = useState(null);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  
  // Options spécifiques au type
  const [showOnMap, setShowOnMap] = useState(postType.id === 'chantier');
  const [chantierType, setChantierType] = useState('');
  const [selectedMetier, setSelectedMetier] = useState(null);
  const [linkedProducts, setLinkedProducts] = useState([]);
  const [linkedServices, setLinkedServices] = useState([]);
  
  // Pour offre/recherche emploi
  const [jobTitle, setJobTitle] = useState('');
  const [jobType, setJobType] = useState('cdi'); // cdi, cdd, interim, stage
  const [salary, setSalary] = useState('');
  
  // Pour produit
  const [productName, setProductName] = useState('');
  const [productPrice, setProductPrice] = useState('');
  
  // Pour prestation
  const [serviceName, setServiceName] = useState('');
  const [servicePrice, setServicePrice] = useState('');
  
  // Tags produits sur les images (Instagram Shopping style)
  const [productTags, setProductTags] = useState({}); // { mediaIndex: [tags] }
  const [editingTagsIndex, setEditingTagsIndex] = useState(null);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((file) => {
      if (!file.type.match(/image\/(jpeg|png|webp|gif)|video\/(mp4|webm)/)) {
        toast.error('Format non supporté');
        return;
      }
      if (media.length >= 10) {
        toast.error('Maximum 10 fichiers');
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        setMedia(prev => [...prev, {
          url: event.target.result,
          type: file.type.startsWith('video') ? 'video' : 'image',
          file
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeMedia = (index) => {
    setMedia(prev => prev.filter((_, i) => i !== index));
  };

  const handleCitySelect = (city) => {
    setLocation(city.name);
    setSelectedCity(city);
    setShowCityDropdown(false);
  };

  const handleSubmit = () => {
    if (!content.trim() && media.length === 0) {
      toast.error('Ajoutez du texte ou une image');
      return;
    }

    // Construire les données selon le type
    const postData = {
      content: content.trim(),
      media: media.map((m, i) => ({ 
        url: m.url, 
        type: m.type, 
        order: i,
        product_tags: productTags[i] || [] // Tags pour cette image
      })),
      location: location || null,
      category: postType.id,
      // Chantier sur la carte
      show_on_map: showOnMap,
      chantier_type: chantierType || null,
      chantier_metier: selectedMetier?.label || null,
      latitude: selectedCity?.lat || null,
      longitude: selectedCity?.lng || null,
      city: selectedCity?.name || location || null,
      // Produits/Services liés
      linked_products: linkedProducts,
      linked_services: linkedServices,
      // Tous les tags produits aplatis
      product_tags: Object.values(productTags).flat(),
    };

    // Ajouter des infos spécifiques selon le type
    if (postType.id === 'produit' && productName) {
      postData.linked_products = [{
        id: crypto.randomUUID(),
        product_id: crypto.randomUUID(),
        product_type: 'product',
        name: productName,
        price: parseFloat(productPrice) || 0,
      }];
    }

    if (postType.id === 'prestation' && serviceName) {
      postData.linked_services = [{
        id: crypto.randomUUID(),
        product_id: crypto.randomUUID(),
        product_type: 'service',
        name: serviceName,
        price: parseFloat(servicePrice) || 0,
      }];
    }

    if ((postType.id === 'offre_emploi' || postType.id === 'recherche_emploi') && jobTitle) {
      postData.content = `${postType.id === 'offre_emploi' ? '📢 OFFRE D\'EMPLOI' : '🔍 RECHERCHE D\'EMPLOI'}\n\n${jobTitle}\n${jobType.toUpperCase()} ${salary ? `• ${salary}` : ''}\n\n${content}`;
    }

    onSubmit(postData);
  };

  // Placeholder selon le type
  const getPlaceholder = () => {
    switch (postType.id) {
      case 'chantier': return 'Décris ton chantier en cours...';
      case 'produit': return 'Présente ce produit...';
      case 'prestation': return 'Décris cette prestation...';
      case 'avant_apres': return 'Raconte cette transformation...';
      case 'offre_emploi': return 'Décris le poste et les missions...';
      case 'recherche_emploi': return 'Présente-toi et tes compétences...';
      case 'conseil': return 'Partage ton conseil ou astuce...';
      case 'question': return 'Pose ta question à la communauté...';
      default: return 'Quoi de neuf ?';
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header du formulaire */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: postType.bgColor }}
          >
            <postType.icon size={24} style={{ color: postType.color }} />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-gray-900">{postType.label}</h3>
            <p className="text-xs text-gray-500">{postType.description}</p>
          </div>
        </div>
      </div>

      {/* Contenu scrollable */}
      <div className="flex-1 overflow-y-auto">
        {/* Profil auteur */}
        <div className="p-4 flex items-center gap-3 border-b border-gray-50">
          {profile?.profile_photo ? (
            <img src={profile.profile_photo} alt="" className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#2F4EA2] to-[#1E40AF] flex items-center justify-center text-white font-bold">
              {profile?.display_name?.[0]?.toUpperCase() || 'U'}
            </div>
          )}
          <div>
            <p className="font-semibold text-gray-900">{profile?.display_name || 'Utilisateur'}</p>
            <p className="text-xs text-gray-500">{profile?.user_type === 'professionnel' ? 'Professionnel' : 'Particulier'}</p>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════
            CHAMPS SPÉCIFIQUES AU TYPE
        ══════════════════════════════════════════════════════════ */}

        {/* CHANTIER : Métier + Type + Localisation + Switch carte */}
        {postType.id === 'chantier' && (
          <div className="p-4 bg-red-50 border-b border-red-100 space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Type de chantier</label>
              <input
                type="text"
                value={chantierType}
                onChange={(e) => setChantierType(e.target.value)}
                placeholder="Ex: Rénovation salle de bain, Piscine..."
                className="w-full px-4 py-2.5 bg-white rounded-xl border border-gray-200 focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Votre métier</label>
              <div className="flex flex-wrap gap-2">
                {METIERS.slice(0, 6).map((metier) => {
                  const Icon = metier.icon;
                  const isSelected = selectedMetier?.id === metier.id;
                  return (
                    <button
                      key={metier.id}
                      onClick={() => setSelectedMetier(isSelected ? null : metier)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        isSelected 
                          ? 'bg-white ring-2 ring-red-500' 
                          : 'bg-white/70 hover:bg-white'
                      }`}
                    >
                      <Icon size={14} style={{ color: metier.color }} />
                      {metier.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Switch afficher sur la carte */}
            <div className="flex items-center justify-between p-3 bg-white rounded-xl">
              <div className="flex items-center gap-3">
                <Map size={20} className="text-red-500" />
                <div>
                  <p className="font-medium text-gray-900 text-sm">Afficher sur la carte</p>
                  <p className="text-xs text-gray-500">Visible dans "Chantiers près de chez toi"</p>
                </div>
              </div>
              <button
                onClick={() => setShowOnMap(!showOnMap)}
                className={`w-12 h-6 rounded-full transition-colors ${showOnMap ? 'bg-red-500' : 'bg-gray-300'}`}
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${showOnMap ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
          </div>
        )}

        {/* PRODUIT : Nom + Prix */}
        {postType.id === 'produit' && (
          <div className="p-4 bg-blue-50 border-b border-blue-100 space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Nom du produit</label>
              <input
                type="text"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                placeholder="Ex: Carrelage marbre blanc, Robinetterie..."
                className="w-full px-4 py-2.5 bg-white rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Prix (€)</label>
              <input
                type="number"
                value={productPrice}
                onChange={(e) => setProductPrice(e.target.value)}
                placeholder="Ex: 45.00"
                className="w-full px-4 py-2.5 bg-white rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        )}

        {/* PRESTATION : Nom + Prix */}
        {postType.id === 'prestation' && (
          <div className="p-4 bg-orange-50 border-b border-orange-100 space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Nom de la prestation</label>
              <input
                type="text"
                value={serviceName}
                onChange={(e) => setServiceName(e.target.value)}
                placeholder="Ex: Pose carrelage, Installation PAC..."
                className="w-full px-4 py-2.5 bg-white rounded-xl border border-gray-200 focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Prix à partir de (€)</label>
              <input
                type="number"
                value={servicePrice}
                onChange={(e) => setServicePrice(e.target.value)}
                placeholder="Ex: 500.00"
                className="w-full px-4 py-2.5 bg-white rounded-xl border border-gray-200 focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>
        )}

        {/* OFFRE / RECHERCHE EMPLOI */}
        {(postType.id === 'offre_emploi' || postType.id === 'recherche_emploi') && (
          <div className="p-4 bg-green-50 border-b border-green-100 space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                {postType.id === 'offre_emploi' ? 'Intitulé du poste' : 'Poste recherché'}
              </label>
              <input
                type="text"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                placeholder="Ex: Plombier chauffagiste, Électricien..."
                className="w-full px-4 py-2.5 bg-white rounded-xl border border-gray-200 focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700 mb-2 block">Type de contrat</label>
                <select
                  value={jobType}
                  onChange={(e) => setJobType(e.target.value)}
                  className="w-full px-4 py-2.5 bg-white rounded-xl border border-gray-200 focus:ring-2 focus:ring-green-500"
                >
                  <option value="cdi">CDI</option>
                  <option value="cdd">CDD</option>
                  <option value="interim">Intérim</option>
                  <option value="stage">Stage</option>
                  <option value="alternance">Alternance</option>
                </select>
              </div>
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700 mb-2 block">Salaire</label>
                <input
                  type="text"
                  value={salary}
                  onChange={(e) => setSalary(e.target.value)}
                  placeholder="Ex: 2500€/mois"
                  className="w-full px-4 py-2.5 bg-white rounded-xl border border-gray-200 focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>
          </div>
        )}

        {/* Zone de texte principale */}
        <div className="p-4">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={getPlaceholder()}
            className="w-full min-h-32 resize-none border-0 focus:ring-0 text-gray-900 placeholder-gray-400 text-base leading-relaxed"
            data-testid="post-content-input"
          />
        </div>

        {/* Aperçu médias avec tags produits */}
        {media.length > 0 && (
          <div className="p-4 border-t border-gray-100">
            {/* Indicateur tags produits pour les PRO */}
            {profile?.user_type === 'professionnel' && media.some(m => m.type === 'image') && (
              <div className="mb-3 p-3 bg-blue-50 rounded-xl flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Tag size={20} className="text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900 text-sm">Tags produits</p>
                  <p className="text-xs text-gray-500">Clique sur une image pour taguer tes produits</p>
                </div>
                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                  {Object.values(productTags).flat().length} tag(s)
                </span>
              </div>
            )}
            
            <div className={`grid gap-2 ${
              media.length === 1 ? 'grid-cols-1' :
              media.length === 2 ? 'grid-cols-2' :
              'grid-cols-2 md:grid-cols-3'
            }`}>
              {media.map((m, index) => (
                <div key={index} className="relative aspect-square rounded-xl overflow-hidden bg-gray-100">
                  {m.type === 'video' ? (
                    <video src={m.url} className="w-full h-full object-cover" />
                  ) : profile?.user_type === 'professionnel' ? (
                    // Image avec overlay de tags pour les PRO
                    <ProductTagOverlay
                      imageUrl={m.url}
                      tags={productTags[index] || []}
                      isEditing={true}
                      onAddTag={(tag) => {
                        setProductTags(prev => ({
                          ...prev,
                          [index]: [...(prev[index] || []), tag]
                        }));
                      }}
                      onRemoveTag={(tagId) => {
                        setProductTags(prev => ({
                          ...prev,
                          [index]: (prev[index] || []).filter(t => t.id !== tagId)
                        }));
                      }}
                    />
                  ) : (
                    <img src={m.url} alt="" className="w-full h-full object-cover" />
                  )}
                  
                  {/* Bouton supprimer */}
                  <button
                    onClick={() => {
                      removeMedia(index);
                      // Supprimer aussi les tags de cette image
                      setProductTags(prev => {
                        const newTags = { ...prev };
                        delete newTags[index];
                        return newTags;
                      });
                    }}
                    className="absolute top-2 right-2 w-7 h-7 bg-black/60 rounded-full flex items-center justify-center text-white hover:bg-black/80 z-40"
                  >
                    <X size={16} />
                  </button>
                  
                  {/* Badge nombre de tags */}
                  {productTags[index]?.length > 0 && (
                    <div className="absolute bottom-2 left-2 px-2 py-1 bg-blue-600 rounded-full text-white text-xs flex items-center gap-1 z-40">
                      <Tag size={10} />
                      {productTags[index].length} produit(s)
                    </div>
                  )}
                  
                  {m.type === 'video' && (
                    <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/60 rounded text-white text-xs flex items-center gap-1">
                      <Video size={12} />
                      Vidéo
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Localisation */}
        <div className="p-4 border-t border-gray-100">
          <label className="text-sm font-medium text-gray-700 mb-2 block flex items-center gap-2">
            <MapPin size={16} className="text-red-500" />
            Localisation
          </label>
          <div className="relative">
            <input
              type="text"
              value={location}
              onChange={(e) => {
                setLocation(e.target.value);
                setShowCityDropdown(true);
                setSelectedCity(null);
              }}
              onFocus={() => setShowCityDropdown(true)}
              placeholder="Ville ou adresse..."
              className="w-full px-4 py-2.5 bg-gray-50 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#2F4EA2]"
            />
            {/* Dropdown villes */}
            <AnimatePresence>
              {showCityDropdown && location.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute z-10 w-full mt-1 bg-white rounded-xl shadow-lg border border-gray-200 max-h-48 overflow-y-auto"
                >
                  {VILLES.filter(v => 
                    v.name.toLowerCase().includes(location.toLowerCase())
                  ).map((city) => (
                    <button
                      key={city.name}
                      onClick={() => handleCitySelect(city)}
                      className="w-full px-4 py-2.5 text-left hover:bg-gray-50 flex items-center gap-2"
                    >
                      <MapPin size={14} className="text-gray-400" />
                      <span>{city.name}</span>
                      {selectedCity?.name === city.name && (
                        <Check size={14} className="ml-auto text-green-500" />
                      )}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {selectedCity && showOnMap && (
            <p className="mt-2 text-xs text-green-600 flex items-center gap-1">
              <Check size={12} />
              Ce {postType.id === 'chantier' ? 'chantier' : 'post'} sera visible sur la carte
            </p>
          )}
        </div>
      </div>

      {/* Barre d'actions en bas */}
      <div className="border-t border-gray-200 bg-white p-4">
        <div className="flex items-center gap-2 mb-4">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
          >
            <Image size={18} className="text-green-600" />
            <span className="text-sm font-medium">Photo</span>
          </button>
          <button
            onClick={() => {
              fileInputRef.current.accept = 'video/mp4,video/webm';
              fileInputRef.current?.click();
            }}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
          >
            <Video size={18} className="text-purple-600" />
            <span className="text-sm font-medium">Vidéo</span>
          </button>
          <button
            onClick={() => setContent(prev => prev + ' #')}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
          >
            <Hash size={18} className="text-blue-600" />
            <span className="text-sm font-medium">Tag</span>
          </button>
        </div>
        
        <Button
          onClick={handleSubmit}
          disabled={loading || (!content.trim() && media.length === 0)}
          className="w-full py-3 rounded-xl font-semibold"
          style={{ background: `linear-gradient(135deg, ${postType.color}, ${postType.color}dd)` }}
        >
          {loading ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
          ) : (
            <>
              <Send size={18} className="mr-2" />
              Publier
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════
export default function CreatePostPage() {
  const navigate = useNavigate();
  const { profile } = useOutletContext() || {};
  
  const [step, setStep] = useState(1); // 1 = choix type, 2 = formulaire
  const [selectedType, setSelectedType] = useState(null);
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem('ecosystem_token');
  
  // Déterminer le type d'utilisateur (particulier ou professionnel)
  const userType = profile?.user_type || 'particulier';

  // ══════════════════════════════════════════════════════════════════════════════
  // SI NON CONNECTÉ → Afficher la page de certification
  // ══════════════════════════════════════════════════════════════════════════════
  if (!token) {
    return <SocialCertificationPage />;
  }

  const handleSelectType = (type) => {
    setSelectedType(type);
    setStep(2);
  };

  const handleSubmit = async (postData) => {
    setLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          post_type: postData.media?.length > 1 ? 'carousel' : (postData.media?.length === 1 ? 'photo' : 'text'),
          ...postData
        })
      });
      
      if (res.ok) {
        toast.success('Publication créée !');
        navigate('/reseau');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Erreur lors de la publication');
      }
    } catch (e) {
      console.error('Error creating post:', e);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="create-post-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
          <button 
            onClick={() => step === 1 ? navigate(-1) : setStep(1)} 
            className="p-2 -ml-2 hover:bg-gray-100 rounded-xl"
          >
            <ChevronLeft size={24} />
          </button>
          <h1 className="font-bold text-gray-900">
            {step === 1 ? 'Nouvelle publication' : selectedType?.label}
          </h1>
          <div className="w-10" /> {/* Spacer */}
        </div>
        
        {/* Indicateur de type d'utilisateur */}
        <div className="max-w-2xl mx-auto px-4 pb-3">
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${
            userType === 'professionnel' 
              ? 'bg-orange-100 text-orange-700' 
              : 'bg-blue-100 text-blue-700'
          }`}>
            {userType === 'professionnel' ? (
              <>
                <Briefcase size={12} />
                Compte Professionnel
              </>
            ) : (
              <>
                <Users size={12} />
                Compte Particulier
              </>
            )}
          </div>
        </div>
      </header>

      {/* Contenu */}
      <div className="flex-1 max-w-2xl mx-auto w-full bg-white">
        <AnimatePresence mode="wait">
          {step === 1 ? (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <TypeSelectionStep 
                userType={userType} 
                onSelectType={handleSelectType} 
              />
            </motion.div>
          ) : (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="h-[calc(100vh-120px)]"
            >
              <PostFormStep
                postType={selectedType}
                profile={profile}
                onBack={() => setStep(1)}
                onSubmit={handleSubmit}
                loading={loading}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
